import React from "react";
import { DialogDescription } from "@/components/ui/dialog";

type Block = { type: "p"; lines: string[] } | { type: "ul"; items: string[] };

const LIST_ITEM = /^\s*[-•]\s+(.*)$/;

/** Splits translation text into paragraphs and bullet lists.
 * Blank lines separate blocks; consecutive lines starting with "- " form one list. */
const parseInfoText = (text: string): Block[] => {
  const blocks: Block[] = [];
  for (const line of text.split("\n")) {
    if (line.trim() === "") {
      blocks.push({ type: "p", lines: [] }); // block separator, dropped below
      continue;
    }
    const item = LIST_ITEM.exec(line);
    const last = blocks[blocks.length - 1];
    if (item) {
      if (last?.type === "ul") last.items.push(item[1]);
      else blocks.push({ type: "ul", items: [item[1]] });
    } else if (last?.type === "p") {
      last.lines.push(line);
    } else {
      blocks.push({ type: "p", lines: [line] });
    }
  }
  return blocks.filter((b) => (b.type === "ul" ? b.items.length > 0 : b.lines.length > 0));
};

/** Body of an info dialog: paragraphs as <p>, "- " lines as one uniform <ul>. */
export const InfoText: React.FC<{ text: string; children?: React.ReactNode }> = ({ text, children }) => (
  <DialogDescription asChild>
    <div className="space-y-3">
      {parseInfoText(text).map((block, i) =>
        block.type === "ul" ? (
          <ul key={i} className="list-disc list-outside pl-5 space-y-1">
            {block.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            {block.lines.map((line, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </p>
        ),
      )}
      {children}
    </div>
  </DialogDescription>
);
